import { useEffect, useRef } from 'react'
import { useAppStore } from './store'
import { encodeUrlState } from './urlState'

/**
 * Keeps the address bar and the store in sync in both directions.
 *
 * Three things have to hold at once:
 *
 * 1. **A cold load with a query string restores the exact view.** The URL is the
 *    source of truth on the first commit, so the writer below skips its first
 *    run rather than stamping the empty initial store over it.
 * 2. **Back and forward work.** A settled change pushes a history entry.
 *    Changes arriving inside {@link COALESCE_MS} of the previous one replace it
 *    instead, so dragging the power slider or ticking four operators in a row
 *    costs one back press rather than four. The text query is already debounced
 *    before it reaches the store, so it arrives here pre-settled.
 * 3. **It does not fight the map.** After a popstate the store is rebuilt from
 *    the URL, so the writer finds the encoded state already equal to what the
 *    history entry holds and writes nothing. That means the map can move the
 *    selection (a marker click) or the user can move it (a back press) without
 *    either one echoing back at the other.
 *
 * A URL that decodes to something other than what it says - a dropped operator,
 * a stray `utm_source`, a negative `pmin` - is normalised in place with
 * `replaceState`, never pushed, so a hand-edited link does not leave a phantom
 * entry between the user and the page they came from.
 */

/** Changes closer together than this share one history entry. */
export const COALESCE_MS = 400

const searchToHref = (search: string): string =>
  `${window.location.pathname}${search === '' ? '' : `?${search}`}`

const currentHref = (): string => `${window.location.pathname}${window.location.search}`

export const useUrlSync = (): void => {
  const hydrateFromSearch = useAppStore((state) => state.hydrateFromSearch)
  const filters = useAppStore((state) => state.filters)
  const query = useAppStore((state) => state.query)
  const selectedSiteId = useAppStore((state) => state.selectedSiteId)

  /** The encoded state the current history entry stands for. */
  const entrySearch = useRef<string | null>(null)
  /** Skips the writer's first run, while the URL still outranks the store. */
  const writerArmed = useRef(false)
  const lastWriteAt = useRef(0)

  useEffect(() => {
    const adopt = (): void => {
      hydrateFromSearch(window.location.search)

      // Re-encode rather than trusting the raw string: this is what the store
      // now holds, and it is what the writer will compare against.
      const normalised = useAppStore.getState().toSearch()
      entrySearch.current = normalised
      if (searchToHref(normalised) !== currentHref())
        window.history.replaceState(null, '', searchToHref(normalised))
    }

    adopt()
    window.addEventListener('popstate', adopt)
    return () => window.removeEventListener('popstate', adopt)
  }, [hydrateFromSearch])

  useEffect(() => {
    if (!writerArmed.current) {
      writerArmed.current = true
      return
    }

    const next = encodeUrlState({ filters, query, selectedSiteId })
    if (next === entrySearch.current) return

    const now = Date.now()
    const coalesce = now - lastWriteAt.current < COALESCE_MS
    lastWriteAt.current = now
    entrySearch.current = next

    const href = searchToHref(next)
    if (coalesce) window.history.replaceState(null, '', href)
    else window.history.pushState(null, '', href)
  }, [filters, query, selectedSiteId])
}
